/**
 * v1.2 红旗 1：销售流程 5 个 step 的创建模态（合同 / 收款 / 邮寄 / 资料 / 订单）
 *
 * 每个 modal 走 useCustomerData hook：
 *   - 客户身份 / 联系方式 / 地址 / 报名版型 / 销售员 等"已填字段"自动 prefill
 *   - prefill 字段 disabled，**不可手动输入覆盖**（修改要去客户主表）
 *   - 客户主表改了 → 重新打开 modal 立即同步（React Query invalidate）
 *
 * 触发：客户详情页 SalesFlowProgress 当前 step 旁的「+ 新建」按钮。
 */
import { useEffect, useState } from 'react';
import { Modal, Input, InputNumber, DatePicker, Select, message, Radio } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { $ } from '../lib/api';
import { useCustomerData } from '../lib/useCustomerData';
import { Btn, Pill } from './ui';

interface BaseProps {
  open: boolean;
  onClose: () => void;
  customerId: number;
}

// ──────────────────────────────────────────────────────────────────
// Step 3 签订合同
// ──────────────────────────────────────────────────────────────────
export function ContractCreateModal({ open, onClose, customerId }: BaseProps) {
  const qc = useQueryClient();
  const { customer, prefill } = useCustomerData(customerId);
  // 薪酬阶段1·选班型带价：班型下拉（含定死价），选班型自动带出锁定金额；金额不再自由填（后端也按 class_type_config 权威取价）。
  const { data: classTypes } = useQuery<any[]>({
    queryKey: ['salary-class-types'],
    queryFn: () => $.get('/salary/class-types'),
    enabled: open,
    staleTime: 60_000,
  });
  const [form, setForm] = useState({
    serviceType: '',
    servicePackage: '',
    amount: 0,
    serviceStartAt: null as Dayjs | null,
    serviceEndAt: null as Dayjs | null,
    notes: '',
  });
  // 选班型 → 自动带出该班型定死价（金额锁定）。serviceType 存班型键（=后端 class_type_config.class_type_key）。
  const pickClassType = (key: string) => {
    const ct = (classTypes ?? []).find((c) => c.classTypeKey === key);
    setForm((f) => ({ ...f, serviceType: key, amount: ct ? Number(ct.standardPrice) : 0 }));
  };

  const submit = useMutation({
    mutationFn: () => $.post('/contracts', {
      customerId,
      serviceType: form.serviceType,
      servicePackage: form.servicePackage || undefined,
      amount: form.amount,
      serviceStartAt: form.serviceStartAt?.toISOString(),
      serviceEndAt: form.serviceEndAt?.toISOString(),
      notes: form.notes || undefined,
    }),
    onSuccess: () => {
      message.success('合同已创建');
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
      onClose();
    },
    onError: (e: any) => message.error(e?.message ?? '创建失败'),
  });

  return (
    <Modal open={open} onCancel={onClose} title="新建合同（Step 3）" width={560}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary"
               disabled={!form.serviceType || !form.amount || !form.serviceStartAt || !form.serviceEndAt || submit.isPending}
               onClick={() => submit.mutate()}>
            {submit.isPending ? '提交中…' : '提交'}
          </Btn>
        </>
      }
    >
      <PrefillPanel customer={customer} prefill={prefill} />
      <FieldRow label="班型 *（选班型自动带出定死价）">
        <Select value={form.serviceType || undefined}
                onChange={(v) => pickClassType(String(v))}
                style={{ width: '100%' }}
                placeholder="选择班型"
                options={(classTypes ?? []).map((c) => ({
                  value: c.classTypeKey,
                  label: `${c.label} · ¥${Number(c.standardPrice).toLocaleString()}`,
                }))}
        />
      </FieldRow>
      <FieldRow label="服务套餐">
        <Input value={form.servicePackage} onChange={(e) => setForm({ ...form, servicePackage: e.target.value })}
               placeholder="例：年度基础版" />
      </FieldRow>
      <FieldRow label="金额 ¥ *（按班型定死价自动带出、不可改）">
        <InputNumber value={form.amount} disabled style={{ width: '100%' }} />
      </FieldRow>
      <FieldRow label="服务起 *">
        <DatePicker value={form.serviceStartAt} onChange={(v) => setForm({ ...form, serviceStartAt: v })}
                    style={{ width: '100%' }} />
      </FieldRow>
      <FieldRow label="服务止 *">
        <DatePicker value={form.serviceEndAt} onChange={(v) => setForm({ ...form, serviceEndAt: v })}
                    style={{ width: '100%' }} />
      </FieldRow>
      {/* B1 件2：去掉销售手填「交付负责人」——真实交付负责人=成单后教务系统自动分配，在客户详情只读展示 */}
      <FieldRow label="备注">
        <Input.TextArea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </FieldRow>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────
// Step 5 定金收账
// ──────────────────────────────────────────────────────────────────
export function PaymentCreateModal({ open, onClose, customerId }: BaseProps) {
  const qc = useQueryClient();
  const { customer, prefill } = useCustomerData(customerId);
  const { data: contracts } = useQuery<any>({
    queryKey: ['customer-contracts', customerId],
    queryFn: () => $.get('/contracts', { customerId, pageSize: 20 }),
    enabled: open,
  });

  const [form, setForm] = useState({
    contractId: 0,
    orderType: 'deposit' as 'full' | 'deposit',
    totalDue: 0,
    depositAmount: 0,
    balanceDueAt: null as Dayjs | null,
  });

  useEffect(() => {
    if (!contracts?.items?.length) return;
    const latest = contracts.items[0];
    setForm((f) => ({
      ...f,
      contractId: f.contractId || Number(latest.id),
      totalDue: f.totalDue || Number(latest.amount ?? 0),
      // 复用客户的 deposit_chosen（来自 L7 决策卡）
      orderType: (customer?.depositChosen === 'with') ? 'deposit' : (customer?.depositChosen === 'without' ? 'full' : f.orderType),
    }));
  }, [contracts, customer?.depositChosen]);

  const submit = useMutation({
    mutationFn: () => $.post('/payments', {
      contractId: form.contractId,
      orderType: form.orderType,
      totalDue: form.totalDue,
      depositAmount: form.orderType === 'deposit' ? form.depositAmount : undefined,
      balanceDueAt: form.balanceDueAt?.toISOString(),
    }),
    onSuccess: () => {
      message.success('收款单已创建');
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
      onClose();
    },
    // 透传后端 DTO 校验报错（如「合同 ID 必填」），否则用户看不到具体原因
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '创建失败'),
  });

  const noContract = contracts != null && !(contracts.items?.length);

  return (
    <Modal open={open} onCancel={onClose} title="新建收款单（Step 5）" width={560}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary"
               disabled={!form.contractId || !form.totalDue || submit.isPending}
               onClick={() => submit.mutate()}>
            {submit.isPending ? '提交中…' : '提交'}
          </Btn>
        </>
      }
    >
      <PrefillPanel customer={customer} prefill={prefill} />
      {noContract && (
        <div style={{ fontSize: 12, color: '#ad6800', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '8px 10px', marginBottom: 12 }}>
          ⚠️ 该客户还没有合同，无法建收款单。请先到「Step 3 创建合同」，再回此处收款。
        </div>
      )}
      <FieldRow label="合同 *">
        <Select value={form.contractId || undefined}
                onChange={(v) => {
                  const cid = Number(v);
                  const ct = (contracts?.items ?? []).find((c: any) => Number(c.id) === cid);
                  const amt = ct ? Number(ct.amount) : 0;
                  // 评审#8：切换合同后把定金夹紧到不超过新应收额，避免残留旧定金>新应收触发 400
                  setForm((f) => ({ ...f, contractId: cid, totalDue: amt, depositAmount: Math.min(f.depositAmount, amt) }));
                }}
                style={{ width: '100%' }}
                placeholder="选择该客户的合同"
                options={(contracts?.items ?? []).map((c: any) => ({
                  value: Number(c.id),
                  label: `${c.contractCode} · ¥${Number(c.amount).toLocaleString()}`,
                }))}
        />
      </FieldRow>
      <FieldRow label="类型 *">
        <Radio.Group value={form.orderType} onChange={(e) => setForm({ ...form, orderType: e.target.value })}>
          <Radio value="full">全款</Radio>
          <Radio value="deposit">定金</Radio>
        </Radio.Group>
        {customer?.depositChosen && (
          <div style={{ fontSize: 12, color: 'var(--text-3, #8F959E)', marginTop: 4 }}>
            L7 决策卡：{customer.depositChosen === 'with' ? '有定金' : '无定金'}（已自动选）
          </div>
        )}
      </FieldRow>
      <FieldRow label="应收金额 ¥ *（=合同金额/班型定死价，不可改）">
        <InputNumber value={form.totalDue} disabled style={{ width: '100%' }} />
      </FieldRow>
      {form.orderType === 'deposit' && (
        <>
          <FieldRow label="定金金额 ¥">
            <InputNumber value={form.depositAmount} onChange={(v) => setForm({ ...form, depositAmount: Number(v ?? 0) })}
                         min={0} max={form.totalDue} style={{ width: '100%' }} />
          </FieldRow>
          <FieldRow label="尾款约定时间">
            <DatePicker value={form.balanceDueAt} onChange={(v) => setForm({ ...form, balanceDueAt: v })}
                        style={{ width: '100%' }} />
          </FieldRow>
        </>
      )}
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────
// Step 6 教材邮寄
// ──────────────────────────────────────────────────────────────────
export function ShipmentCreateModal({ open, onClose, customerId }: BaseProps) {
  const qc = useQueryClient();
  const { customer, prefill } = useCustomerData(customerId);
  const [form, setForm] = useState({
    materials: '',
    address: '',
    recipientName: '',
    notes: '',
  });

  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({
      ...f,
      address: f.address || prefill.address,
      recipientName: f.recipientName || prefill.realName,
    }));
  }, [prefill]);

  const submit = useMutation({
    mutationFn: () => $.post('/shipments', {
      customerId,
      materials: form.materials.split('\n').map((s) => s.trim()).filter(Boolean),
      address: form.address,
      recipientName: form.recipientName,
      notes: form.notes || undefined,
    }),
    onSuccess: () => {
      message.success('邮寄单已创建');
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
      onClose();
    },
    onError: (e: any) => message.error(e?.message ?? '创建失败'),
  });

  return (
    <Modal open={open} onCancel={onClose} title="新建教材邮寄（Step 6）" width={560}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary"
               disabled={!form.materials || !form.address || !form.recipientName || submit.isPending}
               onClick={() => submit.mutate()}>
            {submit.isPending ? '提交中…' : '提交'}
          </Btn>
        </>
      }
    >
      <PrefillPanel customer={customer} prefill={prefill} highlight={['address', 'realName']} />
      <FieldRow label="教材清单 *（每行一项）">
        <Input.TextArea autoFocus value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })}
                        rows={4} placeholder="教材包 A\n练习册 B" />
      </FieldRow>
      <FieldRow label="收件地址 *（已 prefill）">
        <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
               disabled={!!prefill?.address}
               placeholder={prefill?.address ? '' : '客户主表未填，请在客户详情先补'} />
      </FieldRow>
      <FieldRow label="收件人 *（已 prefill）">
        <Input value={form.recipientName} onChange={(e) => setForm({ ...form, recipientName: e.target.value })}
               disabled={!!prefill?.realName}
               placeholder={prefill?.realName ? '' : '客户主表未填真实姓名'} />
      </FieldRow>
      <FieldRow label="备注">
        <Input.TextArea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </FieldRow>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────
// Step 7 学员资料
// ──────────────────────────────────────────────────────────────────
export function MaterialCreateModal({ open, onClose, customerId }: BaseProps) {
  const qc = useQueryClient();
  const { customer, prefill } = useCustomerData(customerId);
  const [form, setForm] = useState({
    idCardVerified: false,
    educationVerified: false,
    photoVerified: false,
    notes: '',
  });

  const submit = useMutation({
    mutationFn: () => $.post('/materials', {
      customerId,
      idCardVerified: form.idCardVerified,
      educationVerified: form.educationVerified,
      photoVerified: form.photoVerified,
      notes: form.notes || undefined,
    }),
    onSuccess: () => {
      message.success('学员资料已创建');
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
      onClose();
    },
    onError: (e: any) => message.error(e?.message ?? '创建失败'),
  });

  return (
    <Modal open={open} onCancel={onClose} title="新建学员资料（Step 7）" width={520}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary" disabled={submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? '提交中…' : '提交'}
          </Btn>
        </>
      }
    >
      <PrefillPanel customer={customer} prefill={prefill} highlight={['realName']} />
      <FieldRow label="身份证已核验">
        <Radio.Group value={form.idCardVerified ? 'true' : 'false'}
                     onChange={(e) => setForm({ ...form, idCardVerified: e.target.value === 'true' })}>
          <Radio value="true">是</Radio><Radio value="false">否</Radio>
        </Radio.Group>
      </FieldRow>
      <FieldRow label="学历已核验">
        <Radio.Group value={form.educationVerified ? 'true' : 'false'}
                     onChange={(e) => setForm({ ...form, educationVerified: e.target.value === 'true' })}>
          <Radio value="true">是</Radio><Radio value="false">否</Radio>
        </Radio.Group>
      </FieldRow>
      <FieldRow label="照片已核验">
        <Radio.Group value={form.photoVerified ? 'true' : 'false'}
                     onChange={(e) => setForm({ ...form, photoVerified: e.target.value === 'true' })}>
          <Radio value="true">是</Radio><Radio value="false">否</Radio>
        </Radio.Group>
      </FieldRow>
      <FieldRow label="备注">
        <Input.TextArea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </FieldRow>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────
// Step 8 提交订单
// ──────────────────────────────────────────────────────────────────
export function OrderCreateModal({ open, onClose, customerId }: BaseProps) {
  const qc = useQueryClient();
  const { customer, prefill } = useCustomerData(customerId);
  const { data: contracts } = useQuery<any>({
    queryKey: ['customer-contracts', customerId],
    queryFn: () => $.get('/contracts', { customerId, pageSize: 10 }),
    enabled: open,
  });
  const { data: payments } = useQuery<any>({
    queryKey: ['customer-payments', customerId],
    queryFn: () => $.get('/payments', { customerId, pageSize: 10 }),
    enabled: open,
  });
  const { data: materials } = useQuery<any>({
    queryKey: ['customer-materials', customerId],
    queryFn: () => $.get('/materials', { customerId, pageSize: 10 }),
    enabled: open,
  });

  const [form, setForm] = useState({
    contractId: 0,
    paymentId: 0,
    materialId: 0,
    notes: '',
  });

  useEffect(() => {
    if (contracts?.items?.[0] && !form.contractId) {
      setForm((f) => ({ ...f, contractId: Number(contracts.items[0].id) }));
    }
    if (payments?.items?.[0] && !form.paymentId) {
      setForm((f) => ({ ...f, paymentId: Number(payments.items[0].id) }));
    }
    if (materials?.items?.[0] && !form.materialId) {
      setForm((f) => ({ ...f, materialId: Number(materials.items[0].id) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, payments, materials]);

  const submit = useMutation({
    mutationFn: () => {
      // classType 从选中合同的 serviceType 带（班型签合同时已定）；后端也兜底，双保险防 class_type 1048
      const selected = (contracts?.items ?? []).find((c: any) => Number(c.id) === form.contractId);
      return $.post('/orders', {
        customerId,
        contractId: form.contractId,
        paymentId: form.paymentId,
        materialId: form.materialId,
        classType: selected?.serviceType,
        notes: form.notes || undefined,
      });
    },
    onSuccess: () => {
      message.success('订单已提交审批');
      qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
      onClose();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '创建失败'),
  });

  return (
    <Modal open={open} onCancel={onClose} title="新建订单（Step 8）" width={560}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary"
               disabled={!form.contractId || !form.paymentId || !form.materialId || submit.isPending}
               onClick={() => submit.mutate()}>
            {submit.isPending ? '提交中…' : '提交'}
          </Btn>
        </>
      }
    >
      <PrefillPanel customer={customer} prefill={prefill} />
      <FieldRow label="合同 *">
        <Select value={form.contractId || undefined}
                onChange={(v) => setForm({ ...form, contractId: Number(v) })}
                style={{ width: '100%' }}
                placeholder="选合同"
                options={(contracts?.items ?? []).map((c: any) => ({
                  value: Number(c.id), label: `${c.contractCode}`,
                }))}
        />
      </FieldRow>
      <FieldRow label="收款单 *">
        <Select value={form.paymentId || undefined}
                onChange={(v) => setForm({ ...form, paymentId: Number(v) })}
                style={{ width: '100%' }}
                placeholder="选收款单"
                options={(payments?.items ?? []).map((p: any) => ({
                  value: Number(p.id), label: `${p.paymentCode} · ¥${Number(p.totalDue).toLocaleString()}`,
                }))}
        />
      </FieldRow>
      <FieldRow label="学员资料 *">
        <Select value={form.materialId || undefined}
                onChange={(v) => setForm({ ...form, materialId: Number(v) })}
                style={{ width: '100%' }}
                placeholder="选学员资料"
                options={(materials?.items ?? []).map((m: any) => ({
                  value: Number(m.id), label: `${m.materialCode}`,
                }))}
        />
      </FieldRow>
      <FieldRow label="备注">
        <Input.TextArea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </FieldRow>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────
// 共享：prefill 信息条
// ──────────────────────────────────────────────────────────────────
function PrefillPanel({ customer, prefill, highlight = [] }: { customer: any; prefill: any; highlight?: string[] }) {
  if (!customer) return null;
  return (
    <div style={{
      padding: 10, marginBottom: 12,
      background: 'var(--primary-bg, #EAF2FF)', borderRadius: 8,
      fontSize: 12, color: 'var(--text-2, #4E5969)',
    }}>
      <div style={{ marginBottom: 6, color: 'var(--text-3, #8F959E)' }}>
        客户主表已填字段（修改请去客户详情）：
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        <Kv label="姓名" value={prefill?.realName} hl={highlight.includes('realName')} />
        <Kv label="手机" value={prefill?.phone} hl={highlight.includes('phone')} />
        <Kv label="微信" value={prefill?.wechat} />
        <Kv label="地址" value={prefill?.address} hl={highlight.includes('address')} />
        <Kv label="报名版型" value={prefill?.productType} />
        <Kv label="销售员 ID" value={prefill?.ownerId ? `#${prefill.ownerId}` : '—'} />
      </div>
    </div>
  );
}

function Kv({ label, value, hl }: { label: string; value: any; hl?: boolean }) {
  return (
    <div>
      <span style={{ color: 'var(--text-3, #8F959E)' }}>{label}：</span>
      <span style={{
        color: hl ? 'var(--primary, #3370FF)' : 'var(--text-1, #1F2329)',
        fontWeight: hl ? 500 : 400,
      }}>{value || '—'}</span>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-2, #4E5969)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
